/**
 * Organization Management Types
 *
 * Defines all TypeScript interfaces and constants for organization
 * and address management functionality in ConEx 360.
 */

// ============================================================================
// Organization Type Constants
// ============================================================================

export const ORGANIZATION_TYPES = {
  CSP: 'CSP',
  GROWER: 'Grower',
  AGRONOMIST: 'Agronomist',
  ROBOT_MANUFACTURER: 'Robot Manufacturer',
  AG_RETAILER: 'Ag Retailer',
  AG_COOP: 'Ag Co-op',
  DEVICE_OR_SENSOR_MANUFACTURER: 'Device or Sensor Manufacturer',
  AUTONOMY_SERVICE_PROVIDER: 'Autonomy Service Provider',
  APPLICATION_PROVIDER: 'Application Provider',
  SOFTWARE_DEVELOPER: 'Software Developer'
} as const;

export type OrganizationType = typeof ORGANIZATION_TYPES[keyof typeof ORGANIZATION_TYPES];

export const ORGANIZATION_TYPE_OPTIONS = Object.values(ORGANIZATION_TYPES);

// ============================================================================
// Organization Role Constants
// ============================================================================

export const ORGANIZATION_ROLES = {
  CUSTOMER: 'Customer',
  PARTNER: 'Partner',
  SUPPLIER: 'Supplier'
} as const;

export type OrganizationRole = typeof ORGANIZATION_ROLES[keyof typeof ORGANIZATION_ROLES];

export const ORGANIZATION_ROLE_OPTIONS = Object.values(ORGANIZATION_ROLES);

// ============================================================================
// Address Type Constants
// ============================================================================

export const ADDRESS_TYPES = {
  SHIPPING_ADDRESS: 'Shipping Address',
  BILLING_ADDRESS: 'Billing Address',
  HEAD_OFFICE: 'Head Office',
  BRANCH_OFFICE: 'Branch Office',
  WAREHOUSE: 'Warehouse'
} as const;

export type AddressType = typeof ADDRESS_TYPES[keyof typeof ADDRESS_TYPES];

export const ADDRESS_TYPE_OPTIONS = Object.values(ADDRESS_TYPES);

// ============================================================================
// Organization Status Badge Variants
// ============================================================================

export const ORGANIZATION_STATUS_VARIANTS = {
  ACTIVE: 'default',
  ARCHIVED: 'secondary'
} as const;

export type OrganizationStatusVariant = typeof ORGANIZATION_STATUS_VARIANTS[keyof typeof ORGANIZATION_STATUS_VARIANTS];

// ============================================================================
// Organization Interface
// ============================================================================

/**
 * Organization interface representing a business entity in ConEx 360
 */
export interface Organization {
  /** Unique identifier for the organization */
  id: string;

  /** Organization number (backend field) */
  orgNumber?: number;

  /** Organization code (backend field) */
  orgCode?: string;

  /** Organization name (3-100 characters) */
  name: string;

  /** Organization type (CSP, Grower, etc.) - optional for backend compatibility */
  type?: OrganizationType;

  /** Organization role (Customer, Partner, Supplier) - optional for backend compatibility */
  role?: OrganizationRole;

  /** Contact email (optional, must be valid format if provided) */
  email?: string;

  /** Email ID (backend field name for email) */
  emailId?: string;

  /** Contact phone number (combined countryCode + mobileNumber, optional) */
  phone?: string;

  /** Country dialling code e.g. "+1", "+44" (optional) */
  countryCode?: string;

  /** Mobile number digits only (optional) */
  mobileNumber?: string;

  /** Primary address string (optional) */
  address?: string;

  /** Website URL (optional, must be valid URL if provided) */
  website?: string;

  /** Country (backend field) */
  country?: string;

  /** Tax ID or business registration number (optional) */
  taxId?: string;

  /** ID of assigned account manager user (optional) */
  accountManagerId?: string;

  /** Number of employees (optional, must be positive integer) */
  employeeCount?: number;

  /** Array of addresses associated with this organization */
  addresses?: Address[];

  /** Whether the organization is archived (soft-delete) */
  isArchived: boolean;

  /** Archive date (backend field) */
  archiveDate?: string;

  /** Unix timestamp of when organization was created */
  createdAt?: number;

  /** Unix timestamp of when organization was last updated */
  updatedAt?: number;
}

// ============================================================================
// Address Interface
// ============================================================================

/**
 * Address interface representing an address associated with an organization
 */
export interface Address {
  /** Unique identifier for the address */
  id: string;

  /** Address number (backend field) */
  addressNumber?: number;

  /** Organization number (backend field for linking to org) */
  orgNumber?: number;

  /** ID of the organization this address belongs to (frontend field) */
  organizationId?: string;

  /** Address name/label (required) */
  addressName: string;

  /** Type of address (Shipping, Billing, Head Office, etc.) */
  addressType: AddressType;

  /** Contact phone number for this address (10-20 characters, optional) */
  phone?: string;

  /** Door number (backend field) */
  doorNumber?: string;

  /** Address line 1 (backend field) */
  addressLine1?: string;

  /** Address line 2 (backend field) */
  addressLine2?: string;

  /** Neighborhood (backend field) */
  neighborhood?: string;

  /** Street address (frontend field) */
  street?: string;

  /** City name (required) */
  city: string;

  /** State/Province (required) */
  state: string;

  /** Postal code/ZIP code (required) */
  postalCode: string;

  /** Country name (required) */
  country: string;

  /** Whether the address is archived (soft-delete) */
  isArchived?: boolean;

  /** Archive date (backend field) */
  archiveDate?: number;

  /** Unix timestamp of when address was created */
  createdAt?: number;

  /** Unix timestamp of when address was last updated */
  updatedAt?: number;
}

// ============================================================================
// API Request/Response Types — derived from Zod schemas (generatedSchema.ts is source of truth)
// ============================================================================

import type {
  GetOrganizationsRegistry,
  GetOrganizationByNumberRegistry,
  UpsertOrganizationRegistry,
  UpsertOrganizationAddressRegistry,
} from '@generated-schemas'
import type { InferRequest, InferResponse } from '@/types/registryTypes'

export type GetOrganizationsResponse          = InferResponse<typeof GetOrganizationsRegistry>
export type GetOrganizationResponse           = InferResponse<typeof GetOrganizationByNumberRegistry>
export type UpsertOrganizationRequest         = InferRequest<typeof UpsertOrganizationRegistry>['body']
export type UpsertOrganizationResponse        = InferResponse<typeof UpsertOrganizationRegistry>
export type UpsertOrganizationAddressRequest  = InferRequest<typeof UpsertOrganizationAddressRegistry>['body']
export type UpsertOrganizationAddressResponse = InferResponse<typeof UpsertOrganizationAddressRegistry>

export interface ArchiveOrganizationRequest {
  organizationId: string
  isArchived: boolean
}

export type ArchiveOrganizationResponse = boolean

export interface ArchiveOrganizationAddressRequest {
  addressId: string
  isArchived: boolean
}

export type ArchiveOrganizationAddressResponse = boolean
